import { pedirAutorizacionKit } from './autorizacionKit';
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

class ApiClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  // Devuelve el cuerpo JSON tal cual lo manda el backend (la mayoria de las rutas
  // de simple-api.js responden con el objeto/array directo, no envuelto en {data:...}).
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      };
    }

    let response: Response;
    try {
      response = await fetch(url, config);
    } catch (error) {
      console.error('API Error (red):', error);
      throw error;
    }

    const data = await response.json().catch(() => ({}));

    if (response.status === 401) {
      localStorage.removeItem('auth_token');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      throw new Error(data.error || 'No autorizado');
    }

    if (!response.ok) {
      // La respuesta completa viaja en el error (ej. requiere_clave_kit + faltantes)
      const err: any = new Error(data.error || `HTTP ${response.status}`);
      err.data = data;
      err.status = response.status;
      throw err;
    }

    return data as T;
  }

  get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint);
  }

  post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, { method: 'POST', body: JSON.stringify(data ?? {}) });
  }

  put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, { method: 'PUT', body: JSON.stringify(data ?? {}) });
  }

  patch<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, { method: 'PATCH', body: JSON.stringify(data ?? {}) });
  }

  delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export const api = new ApiClient(API_URL);

// Reintenta una venta pidiendo la clave de administrador + observación cuando a un kit le
// faltan piezas (respuesta requiere_clave_kit). Si se cancela, el error original sigue su curso.
async function conAutorizacionKit<T>(enviar: (aut?: { clave: string; observacion: string }) => Promise<T>): Promise<T> {
  let aut: { clave: string; observacion: string } | undefined;
  for (;;) {
    try {
      return await enviar(aut);
    } catch (e: any) {
      if (!e?.data?.requiere_clave_kit) throw e;
      const r = await pedirAutorizacionKit(e.data.faltantes || [], e.data.clave_incorrecta ? 'Clave incorrecta' : (aut ? e.message : ''));
      if (!r) throw e;
      aut = r;
    }
  }
}

// El login es la unica ruta del backend que envuelve su respuesta como {data: {...}}
// 2026-10-22: quién tiene la sesión y verificación de la clave de administrador
export const seguridadApi = {
  yo: () => api.get<{ usuario: string; rol: string; nombre: string }>('/api/yo'),
  verificarClave: (clave: string) => api.post<{ ok: boolean }>('/api/verificar-clave-admin', { clave }),
};

// 2026-10-22 (fases 2 y 3): comprobantes RI/RE y cierre de caja
export const comprobantesCajaApi = {
  listar: (f: { desde?: string; hasta?: string; caja?: string; tipo?: string; q?: string }) => {
    const qs = new URLSearchParams(Object.entries(f).filter(([, v]) => v) as [string, string][]).toString();
    return api.get<any[]>(`/api/comprobantes-caja${qs ? '?' + qs : ''}`);
  },
};
export const cierresCajaApi = {
  resumen: (caja: string, fecha: string) => api.get<any>(`/api/cierres-caja/resumen?caja=${caja}&fecha=${fecha}`),
  historial: (caja: string) => api.get<any[]>(`/api/cierres-caja?caja=${caja}`),
  cerrar: (data: any) => api.post<any>('/api/cierres-caja', data),
  reabrir: (id: number, data: { clave: string; motivo: string }) => api.post<any>(`/api/cierres-caja/${id}/reabrir`, data),
};

export const authApi = {
  login: async (usuario: string, password: string) => {
    const res = await api.post<{ data: { token: string; usuario: string; rol: string } }>('/api/login', { usuario, password });
    return res.data;
  },
};

export const busesApi = {
  getAll: () => api.get<any[]>('/api/buses'),
  getById: (id: string) => api.get<any>(`/api/buses/${id}`),
  create: (data: any) => api.post<any>('/api/buses', data),
  update: (id: string, data: any) => api.put<any>(`/api/buses/${id}`, data),
  delete: (id: string) => api.delete<any>(`/api/buses/${id}`),
};

// Pagos que bajan la deuda a credito de un bus (buses.credito_usado). Cada pago
// tambien queda como ingreso en Caja Chica del lado del backend.
export const pagosCreditoApi = {
  getByPadron: (padron: string) => api.get<any[]>(`/api/buses/${encodeURIComponent(padron)}/pagos-credito`),
  // Ventas a credito con saldo pendiente (con items), para ver el detalle en Creditos
  // Anula un pago de credito mal registrado: la deuda vuelve a las ventas exactas y el
  // ingreso sale de caja. Solo pagos registrados desde el 06/10/2026 (tienen detalle).
  anular: (id: string, data: { clave: string; motivo: string }) =>
    api.post<{ success: boolean }>(`/api/pagos-credito-buses/${id}/anular`, data),
  ventasPendientes: (padron: string) => api.get<any[]>(`/api/buses/${encodeURIComponent(padron)}/ventas-credito-pendientes`),
  crear: (padron: string, data: { monto_productos: number; monto_servicios: number; metodo_pago: string; observaciones?: string; yape_capturas?: string[]; pagado_por?: string }) =>
    api.post<{ pago: any; credito_usado: number; credito_usado_productos: number; credito_usado_servicios: number }>(`/api/buses/${encodeURIComponent(padron)}/pagos-credito`, data),
  // Todos los pagos (cualquier bus), para Reportes/Estadísticas.
  getAll: (params?: { desde?: string; hasta?: string }) => {
    const query = new URLSearchParams();
    if (params?.desde) query.set('desde', params.desde);
    if (params?.hasta) query.set('hasta', params.hasta);
    const qs = query.toString();
    return api.get<any[]>(`/api/pagos-credito-buses${qs ? `?${qs}` : ''}`);
  },
  // Sube comprobante(s) de Yape para un pago de credito ya existente
  subirCapturasYape: (id: string, capturas: string[]) =>
    api.post<{ yape_capturas: string[] }>(`/api/pagos-credito-buses/${id}/yape-capturas`, { capturas }),
  // Deuda antigua: sube credito_usado directo, sin productos/items, para deuda
  // arrastrada de antes de este sistema. Pide clave de administrador y queda
  // registrada en Auditoría.
  agregarDeudaAntigua: (padron: string, data: { clave: string; monto_productos?: number; monto_servicios?: number; motivo?: string }) =>
    api.post<{ success: boolean; credito_usado: number; credito_usado_productos: number; credito_usado_servicios: number }>(`/api/buses/${encodeURIComponent(padron)}/deuda-antigua`, data),
  // Pago automatico del credito NUEVO (con detalle de ventas) de todos los buses de un
  // propietario a la vez -- reparte solo, repuestos primero, de la deuda mas antigua a
  // la mas reciente. El cajero solo ingresa un monto total.
  pagarPorDueno: (dueno: string, data: { monto: number; metodo_pago: string; observaciones?: string; yape_capturas?: string[]; pagado_por?: string }) =>
    api.post<{ dueno: string; monto_aplicado: number; productos_aplicado: number; servicios_aplicado: number;
               buses_tocados: { padron: string; productos_aplicado: number; servicios_aplicado: number; credito_usado: number }[] }>(
      `/api/buses/dueno/${encodeURIComponent(dueno)}/pagar-credito`, data),
};

// Creditos "pasados" (de antes del reset del sistema, o cualquier deuda antigua sin
// venta formal detras): solo nombre de cliente + monto, sin descripcion. El COBRO si se
// puede repartir: la parte que es de piezas entra a Caja Ventas, el resto a Caja Servicios.
export const creditosPasadosApi = {
  getAll: () => api.get<any[]>('/api/creditos-pasados'),
  crear: (data: { cliente_nombre: string; monto: number; notas?: string }) =>
    api.post<any>('/api/creditos-pasados', data),
  pagarCliente: (data: { cliente_nombre: string; monto_ventas: number; monto_servicios: number; metodo_pago: string; observaciones?: string }) =>
    api.post<{ cliente_nombre: string; monto_aplicado: number; ventas_aplicado: number; servicios_aplicado: number }>('/api/creditos-pasados/cliente/pagar', data),
  // Anula un credito antiguo puntual (deja el pendiente en 0). Pide clave de administrador
  // y queda registrado en Auditoría.
  anular: (id: string, data: { clave: string; motivo?: string }) =>
    api.post<{ success: boolean; credito: any }>(`/api/creditos-pasados/${id}/anular`, data),
  // Pagos (cobros) de deuda antigua de un cliente, y anular uno mal registrado (2026-10-23):
  // la deuda vuelve, el ingreso sale de caja y el RI queda ANULADO. Se identifica por su fila de caja.
  pagos: (cliente: string) => api.get<any[]>(`/api/creditos-pasados/pagos?cliente=${encodeURIComponent(cliente)}`),
  anularPago: (cajaId: string, data: { clave: string; motivo: string }) =>
    api.post<{ success: boolean; cliente: string; monto: number; cajas: { caja: string; monto: number; comprobante: string | null }[] }>(`/api/creditos-pasados/pagos/${cajaId}/anular`, data),
};

// Cuota fija de mantenimiento por bus (S/280, independiente de repuestos/stock), se
// cobra dos veces al mes. El monto NUNCA se manda desde el frontend: lo fija el backend.
export const pagosMantenimientoApi = {
  // Buses activos que todavia no registraron el pago del periodo actual, + el periodo y el monto vigente.
  getPendientes: () => api.get<{ periodo: string; monto: number; pendientes: any[] }>('/api/pagos-mantenimiento-buses/pendientes'),
  getByPadron: (padron: string) => api.get<any[]>(`/api/buses/${encodeURIComponent(padron)}/pagos-mantenimiento`),
  crear: (padron: string, data: { periodo?: string; metodo_pago: string; observaciones?: string; yape_capturas?: string[];
                                  confirmo_recibido: boolean; clave?: string; motivo_autorizacion?: string }) =>
    api.post<{ pago: any }>(`/api/buses/${encodeURIComponent(padron)}/pagos-mantenimiento`, data),
  getAll: (periodo?: string) => api.get<any[]>(`/api/pagos-mantenimiento-buses${periodo ? `?periodo=${encodeURIComponent(periodo)}` : ''}`),
  subirCapturasYape: (id: string, capturas: string[]) =>
    api.post<{ yape_capturas: string[] }>(`/api/pagos-mantenimiento-buses/${id}/yape-capturas`, { capturas }),
  // Anula un pago ya registrado (revierte tambien el ingreso en Caja Central). Pide
  // clave de administrador y queda registrado en Auditoría.
  anular: (id: string, data: { clave: string; motivo?: string }) =>
    api.post<{ success: boolean }>(`/api/pagos-mantenimiento-buses/${id}/anular`, data),
  // Mueve un pago ya registrado a la deuda a credito del bus o a un servicio al contado
  // (la plata no sale de caja, solo cambia de caja). Clave de administrador + Auditoria.
  mover: (id: string, data: { clave: string; motivo: string; destino: 'credito' | 'servicio'; monto_productos?: number; monto_servicios?: number; detalle?: string }) =>
    api.post<{ success: boolean }>(`/api/pagos-mantenimiento-buses/${id}/mover`, data),
};

// Ticket de aviso de cobro (se le entrega al cobrador/conductor ANTES de recibir el
// dinero). No registra ningun pago, solo deja constancia de que se avisó al bus.
export const precobranzaMantenimientoApi = {
  crear: (padron: string, periodo?: string, conceptos?: { tipo: string; monto?: number; detalle?: string }[]) =>
    api.post<{ precobranza: any }>(`/api/buses/${encodeURIComponent(padron)}/precobranza-mantenimiento`, { periodo, conceptos }),
  listar: (periodo?: string) =>
    api.get<any[]>(`/api/precobranzas-mantenimiento?periodo=${encodeURIComponent(periodo || '')}`),
  anular: (id: string, data: { clave: string; motivo?: string }) =>
    api.post<{ success: boolean }>(`/api/precobranzas-mantenimiento/${id}/anular`, data),
};

export const mantenimientosApi = {
  getAll: () => api.get<any[]>('/api/mantenimientos'),
  create: (data: any) => api.post<any>('/api/mantenimientos', data),
  update: (id: string, data: any) => api.put<any>(`/api/mantenimientos/${id}`, data),
  // Ventas (repuestos/insumos/materiales) registradas para un mantenimiento
  getVentas: (id: string) => api.get<any[]>(`/api/mantenimientos/${id}/ventas`),
};

export const mecanicosApi = {
  getAll: () => api.get<any[]>('/api/mecanicos'),
  create: (data: any) => api.post<any>('/api/mecanicos', data),
  update: (id: string, data: any) => api.put<any>(`/api/mecanicos/${id}`, data),
  // Marca un solo dia (cuadricula de Asistencia); estado null borra la marca
  marcarAsistencia: (id: string, data: { fecha: string; estado: string | null }) => api.post<any>(`/api/mecanicos/${id}/asistencia`, data),
  // Observacion opcional de un dia ya marcado (texto vacio = borrarla)
  obsAsistencia: (id: string, data: { fecha: string; obs: string }) => api.post<any>(`/api/mecanicos/${id}/asistencia/obs`, data),
  delete: (id: string) => api.delete<any>(`/api/mecanicos/${id}`),
};

export const stockApi = {
  // Vista resumida (usada por dropdowns/selectores)
  getAll: () => api.get<any[]>('/api/stock'),
  // Vista completa con todas las columnas, usada por la pantalla de gestion de Stock.
  // eliminado=true devuelve solo los productos eliminados (soft-delete).
  getFull: (params?: { eliminado?: boolean }) => {
    const query = params?.eliminado ? '?eliminado=true' : '';
    return api.get<any[]>(`/api/stock/full${query}`);
  },
  create: (data: any) => api.post<any>('/api/stock', data),
  update: (id: string, data: any) => api.put<any>(`/api/stock/${id}`, data),
  // Kits (2026-11-02): piezas del kit, marcar/desmarcar y guardar receta, ingresar kits armados
  getKit: (id: string) => api.get<any>(`/api/stock/${id}/kit`),
  guardarKit: (id: string, data: { es_kit: boolean; componentes?: { producto_id: string; cantidad: number }[]; sumar_stock_actual?: boolean }) =>
    api.put<any>(`/api/stock/${id}/kit`, data),
  ingresarKits: (id: string, data: { cantidad: number; observacion?: string }) => api.post<any>(`/api/stock/${id}/ingresar-kits`, data),
};

export const ventasApi = {
  getAll: () => api.get<any[]>('/api/ventas'),
  // Venta simple de un solo producto (formulario clasico)
  create: (data: any) => api.post<any>('/api/ventas', data),
  // Venta con carrito multi-producto (contado/credito/mixto), transaccional en el backend
  // Si un kit va incompleto, el servidor responde requiere_clave_kit: se pide clave + observación y se reintenta
  crearConCarrito: (data: any) => conAutorizacionKit((aut) => api.post<any>('/api/ventas/carrito', aut ? { ...data, autorizacion_kit: aut } : data)),
  // Carga administrativa de una venta pasada (fecha propia, sin ticket, queda en auditoria)
  crearRetroactiva: (data: any) => conAutorizacionKit((aut) => api.post<any>('/api/ventas/retroactiva', aut ? { ...data, autorizacion_kit: aut } : data)),
  // Piezas usadas por bus: abiertas (pendientes de cerrar/imprimir), historial completo, y cierre en lote
  getAbiertasPorBus: (placa: string) => api.get<any[]>(`/api/ventas/bus/${encodeURIComponent(placa)}/abiertas`),
  getHistorialPorBus: (placa: string) => api.get<any[]>(`/api/ventas/bus/${encodeURIComponent(placa)}/historial`),
  cerrarVentasBus: (placa: string) => api.post<any[]>(`/api/ventas/bus/${encodeURIComponent(placa)}/cerrar`),
  // Sube comprobante(s) de Yape para una venta ya existente (captura opcional, se puede
  // completar despues desde el Historial en vez de exigirla al momento de vender)
  subirCapturasYape: (id: string, capturas: string[]) => api.post<{ yape_capturas: string[] }>(`/api/ventas/${id}/yape-capturas`, { capturas }),
  // Cambia el tipo de pago de una venta ya cerrada (contado/yape <-> credito), con clave de
  // administrador -- revierte/agrega la diferencia en Caja Central y en la deuda del bus.
  cambiarTipoPago: (id: string, data: { clave: string; nuevo_tipo_pago: 'efectivo' | 'credito' | 'yape'; placa_bus?: string }) =>
    api.post<any>(`/api/ventas/${id}/cambiar-tipo-pago`, data),
  // Cambia el bus al que quedo asignada una venta (error al elegir el bus en el
  // desplegable). Con clave de administrador -- mueve el saldo de credito pendiente
  // (si lo hay) del bus anterior al nuevo; si ya estaba pagada, no toca Caja Central.
  cambiarBus: (id: string, data: { clave: string; nueva_placa_bus: string }) =>
    api.post<any>(`/api/ventas/${id}/cambiar-bus`, data),
  // Anula una venta registrada por error: revierte deuda/caja/stock y la quita de la lista
  // (queda completa en Auditoria). Pide clave de administrador + motivo.
  anular: (id: string, data: { clave: string; motivo: string }) =>
    api.post<{ success: boolean; numero: string }>(`/api/ventas/${id}/anular`, data),
};

export const clientesApi = {
  getAll: () => api.get<any[]>('/api/clientes'),
  create: (data: any) => api.post<any>('/api/clientes', data),
  update: (id: string, data: any) => api.put<any>(`/api/clientes/${id}`, data),
};

export const cajaChicaApi = {
  getAll: (caja?: 'mantenimiento' | 'ventas' | 'servicios') => api.get<any[]>(`/api/caja-chica${caja ? '?caja=' + caja : ''}`),
  getSaldo: (caja?: 'mantenimiento' | 'ventas' | 'servicios') => api.get<any>(`/api/caja-chica/saldo${caja ? '?caja=' + caja : ''}`),
  create: (data: any) => api.post<any>('/api/caja-chica', data),
  update: (id: string, data: any) => api.put<any>(`/api/caja-chica/${id}`, data),
  delete: (id: string) => api.delete<any>(`/api/caja-chica/${id}`),
};

// Libro de caja de Caja Repuestos (tipo Excel: Fecha | Descripcion | Entrada | Salida | Saldo)
export const libroCajaApi = {
  get: (desde: string, hasta: string) => api.get<any>(`/api/caja-repuestos/libro?desde=${desde}&hasta=${hasta}`),
  anularGasto: (id: string, data: { clave: string; motivo: string }) => api.post<any>(`/api/caja-repuestos/gasto/${id}/anular`, data),
};

// Caja Chica = fondo de gastos con techo fijo (2026-10-07). Reponer completa hasta el techo.
export const cajaChicaFondoApi = {
  get: () => api.get<any>('/api/caja-chica-fondo'),
  reponer: (data: { clave: string; nota?: string }) => api.post<any>('/api/caja-chica-fondo/reponer', data),
};

// Caja Repuestos: compras de repuestos/piezas; cada una sale de Caja Chica.
export const comprasRepuestosApi = {
  getAll: (params?: { desde?: string; hasta?: string }) => {
    const q = new URLSearchParams(); if (params?.desde) q.set('desde', params.desde); if (params?.hasta) q.set('hasta', params.hasta);
    const qs = q.toString(); return api.get<any[]>(`/api/compras-repuestos${qs ? '?' + qs : ''}`);
  },
  create: (data: any) => api.post<any>('/api/compras-repuestos', data),
  anular: (id: string, data: { clave: string; motivo: string }) => api.post<any>(`/api/compras-repuestos/${id}/anular`, data),
};

export const adelantosApi = {
  getAll: () => api.get<any[]>('/api/adelantos'),
  create: (data: any) => api.post<any>('/api/adelantos', data),
  delete: (id: string) => api.delete<any>(`/api/adelantos/${id}`),
};

export const quejasApi = {
  getAll: () => api.get<any[]>('/api/quejas'),
  create: (data: any) => api.post<any>('/api/quejas', data),
  update: (id: string | number, data: any) => api.put<any>(`/api/quejas/${id}`, data),
  delete: (id: string | number) => api.delete<any>(`/api/quejas/${id}`),
};

export const pagosBusesApi = {
  getAll: () => api.get<any[]>('/api/pagos-buses'),
  create: (data: any) => api.post<any>('/api/pagos-buses', data),
  update: (id: string, data: any) => api.put<any>(`/api/pagos-buses/${id}`, data),
  getMovimientos: (controlId: string) => api.get<any[]>(`/api/pagos-buses/${controlId}/movimientos`),
  registrarMovimiento: (controlId: string, data: any) =>
    api.post<any>(`/api/pagos-buses/${controlId}/movimientos`, data),
};

export const auditoriaApi = {
  getAll: (params?: { desde?: string; hasta?: string; tabla?: string }) => {
    const query = new URLSearchParams();
    if (params?.desde) query.set('desde', params.desde);
    if (params?.hasta) query.set('hasta', params.hasta);
    if (params?.tabla) query.set('tabla', params.tabla);
    return api.get<any[]>(`/api/auditoria?${query}`);
  },
};

export const importacionesApi = {
  getAll: () => api.get<any[]>('/api/importaciones'),
  getById: (id: string) => api.get<any>(`/api/importaciones/${id}`),
  crear: (data: { clave: string; nombre_archivo: string; filas: any[] }) =>
    api.post<any>('/api/importaciones', data),
  convertirAVentas: (data: { clave: string; filas: any[] }) =>
    api.post<{ insertadas: number; sumaMonto: number; sumaGanancia: number }>(
      '/api/importaciones/convertir-a-ventas', data
    ),
};

export const dataAntiguaApi = {
  get: (tabla: string) => api.get<any[]>(`/api/data-antigua/${tabla}`),
  // Vista legible (columnas con nombre y tipo + filas ya resueltas) para la pantalla Data Antigua
  legible: (vista: string) => api.get<{ columnas: { key: string; label: string; tipo?: 'fecha' | 'moneda' | 'largo' }[]; filas: any[] }>(`/api/data-antigua-legible/${vista}`),
};

export const planillasApi = {
  getPagadosDelPeriodo: (periodo: string) => api.get<any[]>(`/api/planillas/${encodeURIComponent(periodo)}`),
  getHistorial: () => api.get<any[]>('/api/planillas'),
  guardar: (data: { periodo: string; nomina: any[]; caja: 'mantenimiento' | 'ventas' | 'servicios' }) =>
    api.post<{ procesados: number; omitidos: string[]; total_pagado: number }>('/api/planillas/guardar', data),
  anular: (id: string, data: { clave: string; motivo: string }) => api.post<{ ok: boolean }>(`/api/planillas/${id}/anular`, data),
};

export const stockBajoApi = {
  getAll: () => api.get<any[]>('/api/stock/bajo'),
};

export const dashboardExtraApi = {
  getProductosMasVendidos: (limite = 10) => api.get<any[]>(`/api/dashboard/productos-mas-vendidos?limite=${limite}`),
};

export async function descargarArchivoAutenticado(endpoint: string, nombreArchivo: string) {
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';
  const response = await fetch(`${API_URL}${endpoint}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
  });
  if (!response.ok) throw new Error('No se pudo generar el archivo');
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombreArchivo;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const rankingReparacionesApi = {
  get: (padron: string) => api.get<any>(`/api/buses/${encodeURIComponent(padron)}/ranking-reparaciones`),
};

export const etiquetasCodigoBarrasApi = {
  getLista: () => api.get<any[]>('/api/stock/etiquetas-codigo-barras/lista'),
};

export async function descargarEtiquetasPost(body: { modo: 'todos' | 'seleccionados'; ids?: string[] }, nombreArchivo: string) {
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';
  const response = await fetch(`${API_URL}/api/stock/etiquetas-codigo-barras/pdf`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error('No se pudo generar el PDF de etiquetas');
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombreArchivo;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Genera (o regenera, si ya existia uno de esa fecha) el reporte diario de ventas e
// ingresos, lo sube a Google Drive del lado del servidor, y descarga el PDF resultante
// en el mismo click -- mismo patron que descargarEtiquetasPost.
export async function descargarReporteDiario(fecha: string) {
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';
  const response = await fetch(`${API_URL}/api/reportes/diario/generar`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
    },
    body: JSON.stringify({ fecha }),
  });
  if (!response.ok) throw new Error('No se pudo generar el reporte diario');
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Reporte_Diario_${fecha}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
